import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Users, MessageSquareText, FileText, MessageCircle, Activity, Crown, Ban,
  Euro, Wallet, TrendingUp, ShieldCheck, UserCheck, X, Sparkles,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Vue d'ensemble" },
  { id: "clients", label: "Clients" },
  { id: "revenue", label: "Revenus" },
];

const money = (v, c = "eur") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: (c || "eur").toUpperCase() }).format(v || 0);

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [vipModal, setVipModal] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/revenue").then((r) => setRevenue(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const act = async (fn, id, label) => {
    setBusy(id);
    try {
      await fn();
      toast.success(label);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    } finally {
      setBusy(null);
      setVipModal(null);
    }
  };

  const banUser = (u) => act(() => api.post(`/admin/users/${u.user_id}/ban`), u.user_id, "Client banni");
  const unbanUser = (u) => act(() => api.post(`/admin/users/${u.user_id}/unban`), u.user_id, "Client réactivé");
  const revokeVip = (u) => act(() => api.post(`/admin/users/${u.user_id}/revoke-vip`), u.user_id, "Statut VIP retiré");
  const grantVip = (u, duration) => act(() => api.post(`/admin/users/${u.user_id}/grant-vip`, { duration }), u.user_id, "VIP accordé");

  const filtered = users.filter((u) => {
    if (filter === "vip") return u.is_premium;
    if (filter === "free") return !u.is_premium && u.account_status !== "banned";
    if (filter === "banned") return u.account_status === "banned";
    return true;
  });

  const overviewCards = [
    { label: "Utilisateurs", value: stats?.total_users, icon: Users },
    { label: "Abonnés VIP", value: revenue?.active_subscribers, icon: Crown, accent: true },
    { label: "Revenu total", value: revenue ? money(revenue.total_revenue) : null, icon: Euro },
    { label: "Actifs (30j)", value: stats?.active_30d, icon: Activity },
    { label: "Conversations", value: stats?.conversations, icon: MessageSquareText },
    { label: "Documents", value: stats?.documents, icon: FileText },
    { label: "Messages", value: stats?.messages, icon: MessageCircle },
    { label: "Clients gratuits", value: revenue?.free_users, icon: Users },
  ];

  return (
    <div className="p-5 sm:p-10 max-w-6xl" data-testid="admin-dashboard">
      <div className="flex items-center gap-2 text-gold mb-2">
        <ShieldCheck size={18} />
        <span className="text-xs uppercase tracking-[0.2em] font-semibold">Espace Créateur</span>
      </div>
      <h1 className="font-serif text-4xl text-navy mb-6">Tableau de bord</h1>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-8">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-all duration-200 ${
              tab === t.id ? "border-royal text-navy" : "border-transparent text-slate-400 hover:text-navy"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {overviewCards.map((c) => (
            <div key={c.label} data-testid={`admin-stat-${c.label}`} className={`rounded-lg p-5 border ${c.accent ? "bg-navy text-white border-navy" : "bg-white border-border"}`}>
              <c.icon size={18} className={c.accent ? "text-gold mb-2" : "text-royal mb-2"} />
              <p className={`font-serif text-2xl ${c.accent ? "text-white" : "text-navy"}`}>{c.value ?? "–"}</p>
              <p className={`text-xs mt-1 ${c.accent ? "text-white/60" : "text-slate-500"}`}>{c.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* CLIENTS */}
      {tab === "clients" && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            {[["all", "Tous"], ["vip", "VIP"], ["free", "Gratuits"], ["banned", "Bannis"]].map(([id, label]) => (
              <button
                key={id}
                data-testid={`client-filter-${id}`}
                onClick={() => setFilter(id)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                  filter === id ? "bg-navy text-white border-navy" : "border-border text-slate-600 hover:border-navy/40"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="bg-white border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-bone text-slate-500 text-left">
                <tr>
                  <th className="px-5 py-3 font-medium">Client</th>
                  <th className="px-5 py-3 font-medium">Abonnement</th>
                  <th className="px-5 py-3 font-medium">Compte</th>
                  <th className="px-5 py-3 font-medium">Conv.</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.user_id} data-testid="admin-user-row" className="border-t border-border">
                    <td className="px-5 py-3">
                      <p className="text-navy font-medium">{u.name} {u.is_admin && <span className="text-[10px] bg-gold/20 text-gold px-1.5 py-0.5 rounded ml-1">ADMIN</span>}</p>
                      <p className="text-xs text-slate-400">{u.email}</p>
                    </td>
                    <td className="px-5 py-3">
                      {u.is_premium ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-gold/15 text-gold px-2.5 py-1 rounded-full font-semibold"><Crown size={12} /> VIP</span>
                      ) : (
                        <span className="text-xs text-slate-400">Gratuit</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {u.account_status === "banned" ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-600 px-2.5 py-1 rounded-full font-medium"><Ban size={12} /> Banni</span>
                      ) : (
                        <span className="text-xs text-emerald-600">Actif</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-600">{u.conversation_count}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {u.is_premium ? (
                          <button data-testid={`revoke-vip-${u.user_id}`} disabled={busy === u.user_id} onClick={() => revokeVip(u)} className="text-xs px-3 py-1.5 rounded-md border border-border text-slate-600 hover:border-navy/40 disabled:opacity-40 transition-all">Retirer VIP</button>
                        ) : (
                          <button data-testid={`grant-vip-${u.user_id}`} disabled={busy === u.user_id} onClick={() => setVipModal(u)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-gold hover:bg-gold/90 text-navy font-semibold disabled:opacity-40 transition-all"><Crown size={13} /> Ajouter VIP</button>
                        )}
                        {!u.is_admin && (
                          u.account_status === "banned" ? (
                            <button data-testid={`unban-${u.user_id}`} disabled={busy === u.user_id} onClick={() => unbanUser(u)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-emerald-200 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40 transition-all"><UserCheck size={13} /> Réactiver</button>
                          ) : (
                            <button data-testid={`ban-${u.user_id}`} disabled={busy === u.user_id} onClick={() => banUser(u)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-40 transition-all"><Ban size={13} /> Bannir</button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">Aucun client dans cette catégorie.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* REVENUE */}
      {tab === "revenue" && revenue && (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <RevCard icon={Euro} label="Total encaissé" value={money(revenue.total_revenue, revenue.currency)} accent />
            <RevCard icon={TrendingUp} label="Revenu ce mois" value={money(revenue.month_revenue, revenue.currency)} />
            <RevCard icon={Crown} label="Abonnés actifs" value={revenue.active_subscribers} />
            <RevCard icon={Wallet} label="Paiements réussis" value={revenue.paid_count} />
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">Répartition des offres</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(revenue.by_plan || {}).map(([plan, n]) => (
                <div key={plan} className="bg-white border border-border rounded-lg px-5 py-3">
                  <p className="text-xs text-slate-400 capitalize">{plan === "monthly" ? "Mensuel" : plan === "annual" ? "Annuel" : plan}</p>
                  <p className="font-serif text-2xl text-navy">{n}</p>
                </div>
              ))}
              {Object.keys(revenue.by_plan || {}).length === 0 && <p className="text-slate-400 text-sm">Aucun paiement pour le moment.</p>}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">Derniers paiements</h3>
            <div className="bg-white border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-bone text-slate-500 text-left">
                  <tr>
                    <th className="px-5 py-3 font-medium">Client</th>
                    <th className="px-5 py-3 font-medium">Offre</th>
                    <th className="px-5 py-3 font-medium">Montant</th>
                    <th className="px-5 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(revenue.recent_payments || []).map((p, i) => (
                    <tr key={i} data-testid="payment-row" className="border-t border-border">
                      <td className="px-5 py-3 text-slate-600">{p.email}</td>
                      <td className="px-5 py-3 capitalize text-navy">{p.plan === "monthly" ? "Mensuel" : p.plan === "annual" ? "Annuel" : p.plan}</td>
                      <td className="px-5 py-3 font-medium text-navy">{money(p.amount, p.currency)}</td>
                      <td className="px-5 py-3 text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR") : "–"}</td>
                    </tr>
                  ))}
                  {(revenue.recent_payments || []).length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">Aucun paiement enregistré.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* VIP grant modal */}
      {vipModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setVipModal(null)}>
          <div data-testid="vip-modal" className="bg-white rounded-xl p-7 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-gold"><Crown size={18} /><h3 className="font-serif text-xl text-navy">Accorder le statut VIP</h3></div>
              <button onClick={() => setVipModal(null)} className="text-slate-400 hover:text-navy"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-600 mb-5">Offrir Premium à <span className="font-medium text-navy">{vipModal.name}</span>. Choisissez la durée :</p>
            <div className="space-y-2">
              {[["month", "1 mois"], ["year", "1 an"], ["lifetime", "Illimité (à vie)"]].map(([d, label]) => (
                <button
                  key={d}
                  data-testid={`vip-duration-${d}`}
                  disabled={busy === vipModal.user_id}
                  onClick={() => grantVip(vipModal, d)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md border border-border hover:border-gold hover:bg-gold/5 text-navy font-medium transition-all disabled:opacity-40"
                >
                  <Sparkles size={15} className="text-gold" /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const RevCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-lg p-5 border ${accent ? "bg-navy text-white border-navy" : "bg-white border-border"}`}>
    <Icon size={18} className={accent ? "text-gold mb-2" : "text-royal mb-2"} />
    <p className={`font-serif text-2xl ${accent ? "text-white" : "text-navy"}`}>{value ?? "–"}</p>
    <p className={`text-xs mt-1 ${accent ? "text-white/60" : "text-slate-500"}`}>{label}</p>
  </div>
);
